import React, { useState } from "react";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { QrCode, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * A small QR-code trigger button. Clicking opens a popover showing a QR that encodes `url`.
 * Also shows the URL text with a Copy button + an "Open" link.
 */
export default function QRCodeButton({ url, label = "Scan to view", size = 168, testid, iconOnly = true }) {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  if (!url) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={iconOnly ? "icon" : "sm"}
          className={iconOnly ? "h-8 w-8" : "h-8"}
          title="QR code"
          data-testid={testid || "qr-code-btn"}
        >
          <QrCode size={iconOnly ? 15 : 13} className={iconOnly ? "" : "mr-1.5"} weight="bold" />
          {!iconOnly && "QR"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4">
        <div className="eyebrow mb-2">{label}</div>
        <div className="bg-white border border-border rounded-md p-3 flex items-center justify-center">
          <QRCode
            value={url}
            size={size}
            bgColor="#FFFFFF"
            fgColor="#0A0D14"
            level="M"
            style={{ height: "auto", maxWidth: "100%", width: "100%" }}
          />
        </div>
        <div className="mt-3 text-[10.5px] font-mono text-muted-foreground break-all border-l-2 border-primary pl-2">
          {url}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" className="h-8 flex-1 text-[12px]" onClick={copyUrl}>
            <Copy size={12} className="mr-1.5" /> {copied ? "Copied!" : "Copy Link"}
          </Button>
          <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1">
            <Button size="sm" variant="outline" className="h-8 w-full text-[12px]">
              <ArrowSquareOut size={12} className="mr-1.5" /> Open
            </Button>
          </a>
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground text-center">
          Point your phone camera at the code to open the document instantly.
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline QR code (no popover) for print pages. Renders at fixed size, no chrome.
 */
export function InlineQR({ url, size = 84, caption }) {
  if (!url) return null;
  return (
    <div className="flex flex-col items-center">
      <div className="bg-white p-1.5 border border-black">
        <QRCode
          value={url}
          size={size}
          bgColor="#FFFFFF"
          fgColor="#000000"
          level="M"
          style={{ height: `${size}px`, width: `${size}px`, display: "block" }}
        />
      </div>
      {caption && (
        <div className="text-[8.5px] uppercase tracking-[0.16em] text-gray-500 mt-1"
          style={{ fontFamily: "'Chivo', sans-serif" }}>
          {caption}
        </div>
      )}
    </div>
  );
}
